import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  MoreHorizontal, EyeOff, Flag, Copy, CheckCircle2, Link as LinkIcon, Pencil, Trash2,
} from 'lucide-react';
import { hidePost, buildPostShareUrl } from '../api/socialActions';
import { deletePost } from '../api/posts';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ReportPostDialog from './ReportPostDialog';
import EditPostDialog from './EditPostDialog';

/**
 * Phase 50 · Three-dots menu for each post card.
 *
 * Viewer actions: Copia link · Non mostrare più · Segnala.
 * Owner actions:  Copia link · Modifica · Elimina.
 *
 * Also surfaces the short ID (`NYP-XXXXXX`) with a copy button so
 * users can reference their content when reaching out to support.
 */
const PostActionsMenu = ({ post, onHidden, onUpdated, onDeleted }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [idCopied, setIdCopied] = useState(false);

  const isOwner = !!user && user.id === post?.user_id;
  const shareUrl = buildPostShareUrl(post?.id);
  const shortId = post?.short_id || (post?.id ? `NYP-${post.id.replace(/-/g, '').toUpperCase().slice(0, 6)}` : '');

  const copyId = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(shortId);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1400);
    } catch {
      toast({ title: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copiato', description: shareUrl });
    } catch {
      toast({ title: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  const doHide = async () => {
    try {
      await hidePost(post.id);
      toast({ title: 'Post nascosto', description: 'Non lo vedrai più nel tuo feed.' });
      onHidden?.(post.id);
    } catch (err) {
      toast({
        title: 'Impossibile nascondere',
        description: err?.response?.data?.detail || 'Riprova più tardi',
        variant: 'destructive',
      });
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deletePost(post.id);
      toast({ title: 'Post eliminato', description: 'Il post è stato rimosso definitivamente.' });
      setDeleteOpen(false);
      onDeleted?.(post.id);
    } catch (err) {
      toast({
        title: 'Impossibile eliminare',
        description: err?.response?.data?.detail || 'Riprova più tardi',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="text-muted-foreground hover:text-foreground"
            aria-label="More"
            data-testid={`post-menu-trigger-${post.id}`}
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex items-center justify-between gap-2 py-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">ID post</span>
            <button
              onClick={copyId}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs font-mono hover:bg-secondary"
              data-testid={`post-copy-id-${post.id}`}
            >
              {shortId}
              {idCopied ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
            </button>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={copyLink} data-testid={`post-menu-copy-link-${post.id}`}>
            <LinkIcon className="mr-2 h-4 w-4" /> Copia link
          </DropdownMenuItem>
          {isOwner ? (
            <>
              <DropdownMenuItem onSelect={() => setEditOpen(true)} data-testid={`post-menu-edit-${post.id}`}>
                <Pencil className="mr-2 h-4 w-4" /> Modifica
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setDeleteOpen(true)}
                className="text-red-500 focus:text-red-500"
                data-testid={`post-menu-delete-${post.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Elimina
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem onSelect={doHide} data-testid={`post-menu-hide-${post.id}`}>
                <EyeOff className="mr-2 h-4 w-4" /> Non mostrare più
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setReportOpen(true)}
                className="text-red-500 focus:text-red-500"
                data-testid={`post-menu-report-${post.id}`}
              >
                <Flag className="mr-2 h-4 w-4" /> Segnala
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportPostDialog open={reportOpen} onOpenChange={setReportOpen} post={post} />
      {isOwner && (
        <EditPostDialog post={post} open={editOpen} onOpenChange={setEditOpen} onUpdated={onUpdated} />
      )}
      {isOwner && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent data-testid={`post-delete-confirm-${post.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminare questo post?</AlertDialogTitle>
              <AlertDialogDescription>
                Il post e il suo contenuto verranno rimossi definitivamente dal feed.
                Questa azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid={`post-delete-cancel-${post.id}`}>
                Annulla
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); doDelete(); }}
                disabled={deleting}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid={`post-delete-confirm-btn-${post.id}`}
              >
                {deleting ? 'Eliminazione…' : 'Elimina'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default PostActionsMenu;
